import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const PlanComparisonTable = ({ onPlanSelect, selectedPlan }) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: 'Perfect for occasional valuations',
      popular: false,
      features: {
        valuations: '3 per month',
        reports: 'Basic PDF reports',
        comparisons: 'Limited comparisons',
        inventory: 'Not included',
        portfolio: 'Not included',
        alerts: 'Not included',
        support: 'Community support',
        api: 'Not included',
        bulk: 'Not included',
        branding: 'CranePrice branded'
      }
    },
    {
      id: 'dealer_pro',
      name: 'Dealer Pro',
      price: 999,
      description: 'Unlimited valuations for dealers',
      popular: true,
      trial: '14-day free trial',
      features: {
        valuations: 'Unlimited',
        reports: 'Professional PDF reports',
        comparisons: 'Advanced comparisons',
        inventory: 'Full inventory management',
        portfolio: 'Basic portfolio tracking',
        alerts: 'Underpriced inventory alerts',
        support: 'Priority email support',
        api: 'Basic API access',
        bulk: 'Bulk CSV uploads',
        branding: 'Custom branding available'
      }
    },
    {
      id: 'lender_pro',
      name: 'Lender Pro',
      price: 3000,
      description: 'Complete solution for lenders',
      popular: false,
      trial: '14-day free trial',
      features: {
        valuations: 'Unlimited',
        reports: 'Insurance-grade reports',
        comparisons: 'Advanced comparisons',
        inventory: 'Full inventory management',
        portfolio: 'Advanced portfolio management',
        alerts: 'Custom alert thresholds',
        support: 'Dedicated account manager',
        api: 'Full API access',
        bulk: 'Batch processing',
        branding: 'White-label options'
      }
    }
  ];

  const featureCategories = [
    {
      name: 'Core Features',
      features: [
        { key: 'valuations', label: 'Monthly Valuations' },
        { key: 'reports', label: 'PDF Reports' },
        { key: 'comparisons', label: 'Crane Comparisons' }
      ]
    },
    {
      name: 'Management Tools',
      features: [
        { key: 'inventory', label: 'Inventory Management' },
        { key: 'portfolio', label: 'Portfolio Management' },
        { key: 'alerts', label: 'Price Alerts' }
      ]
    },
    {
      name: 'Support & Integration',
      features: [
        { key: 'support', label: 'Customer Support' },
        { key: 'api', label: 'API Access' },
        { key: 'bulk', label: 'Bulk Operations' },
        { key: 'branding', label: 'Custom Branding' }
      ]
    }
  ];

  const displayedCategories = showAllFeatures ? featureCategories : featureCategories?.slice(0, 2);

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-muted px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold text-text-primary">Compare Plans</h3>
        <p className="text-sm text-text-secondary mt-1">
          Choose the plan that best fits your business needs
        </p>
      </div>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          {/* Plan Headers */}
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 w-1/4">
                <span className="text-sm font-medium text-text-secondary">Features</span>
              </th>
              {plans?.map((plan) => (
                <th key={plan?.id} className="text-center p-4 relative">
                  {plan?.popular && (
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                      <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
                        Popular
                      </span>
                    </div>
                  )}
                  <div className="pt-2">
                    <h4 className="font-semibold text-text-primary">{plan?.name}</h4>
                    <div className="mt-2">
                      <span className="text-2xl font-bold text-text-primary">
                        ${plan?.price}
                      </span>
                      {plan?.price > 0 && (
                        <span className="text-text-secondary text-sm">/month</span>
                      )}
                    </div>
                    {plan?.trial && (
                      <p className="text-xs text-success mt-1">{plan?.trial}</p>
                    )}
                    <Button
                      variant={selectedPlan === plan?.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPlanSelect(plan?.id)}
                      className="mt-3"
                      fullWidth
                    >
                      {selectedPlan === plan?.id ? "Selected" : "Select"}
                    </Button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Feature Rows */}
          <tbody>
            {displayedCategories?.map((category) => (
              <React.Fragment key={category?.name}>
                <tr className="bg-muted/50">
                  <td colSpan={4} className="px-4 py-2">
                    <span className="text-sm font-medium text-text-primary">
                      {category?.name}
                    </span>
                  </td>
                </tr>
                {category?.features?.map((feature) => (
                  <tr key={feature?.key} className="border-b border-border hover:bg-muted/30">
                    <td className="p-4">
                      <span className="text-sm text-text-primary">{feature?.label}</span>
                    </td>
                    {plans?.map((plan) => (
                      <td key={plan?.id} className="p-4 text-center">
                        <span className="text-sm text-text-secondary">
                          {plan?.features?.[feature?.key]}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* Show More/Less Button */}
        <div className="p-4 text-center border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            iconName={showAllFeatures ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {showAllFeatures ? "Show Less Features" : "Show All Features"}
          </Button>
        </div>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden p-4 space-y-4">
        {plans?.map((plan) => (
          <div
            key={plan?.id}
            className={`border rounded-lg p-4 ${
              selectedPlan === plan?.id
                ? 'border-primary bg-primary/5' :'border-border'
            }`}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="font-semibold text-text-primary">{plan?.name}</h4>
                <div className="mt-1">
                  <span className="text-xl font-bold text-text-primary">
                    ${plan?.price}
                  </span>
                  {plan?.price > 0 && (
                    <span className="text-text-secondary text-sm">/month</span>
                  )}
                </div>
                {plan?.trial && (
                  <p className="text-xs text-success mt-1">{plan?.trial}</p>
                )}
              </div>
              {plan?.popular && (
                <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
                  Popular
                </span>
              )}
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-text-secondary">Valuations:</span>
                <span className="text-sm text-text-primary">{plan?.features?.valuations}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-text-secondary">Reports:</span>
                <span className="text-sm text-text-primary">{plan?.features?.reports}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-text-secondary">Support:</span>
                <span className="text-sm text-text-primary">{plan?.features?.support}</span>
              </div>
            </div>

            <Button
              variant={selectedPlan === plan?.id ? "default" : "outline"}
              size="sm"
              onClick={() => onPlanSelect(plan?.id)}
              fullWidth
            >
              {selectedPlan === plan?.id ? "Selected" : "Select Plan"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanComparisonTable;